/**
 * PulseTruthSurface
 * -----------------
 * Compact truth strip + expandable reconciliation panel. Used by every
 * consumer of the Sales Truth Pipeline — P&L page, Daily Pulse, EOD Pulse
 * — so operators see identical honesty signals wherever sales numbers
 * appear.
 *
 * Expected prop shape (matches `truth_surface` envelope on Pulse responses
 * and the equivalent fields on `/pnl/realtime`):
 *
 *   truthSurface = {
 *     sales_source: "SC_TRANSACTIONS" | "SALES_CAPTURE" | "SC_TRANSACTIONS+SALES_CAPTURE" | "UPLOAD" | "POS" | "NONE",
 *     truth_contract_state: "VALID" | "PARTIAL" | "SUPPRESSED" | "INVALID" | "NOT_ACTIONABLE",
 *     sales_reconciliation: {
 *       state: "VALID" | "INVALID",
 *       reason?: string,
 *       reconciled: boolean,
 *       tolerance: number,
 *       deltas: { core_sales, net_sales, collection }
 *     },
 *     bridges: { core_sales, gross_minus_discount, category_total, net_sales, core_plus_packaging, customer_collection, net_plus_service_plus_tax },
 *     currency: { gross_sales, discounts, net_sales, food_sales, alcohol_sales, beverage_sales, service_charge, packaging, tax }
 *   }
 */
import React from 'react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

const fmt = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const PulseTruthSurface = ({ truthSurface, compact = false, testIdPrefix = 'pulse' }) => {
  if (!truthSurface || !truthSurface.sales_source) return null;

  const srcRaw = truthSurface.sales_source;
  const isMirror = typeof srcRaw === 'string' && srcRaw.includes('SC_TRANSACTIONS');
  const isLive = srcRaw === 'SALES_CAPTURE';
  const isUpload = srcRaw === 'UPLOAD';
  const isPos = srcRaw === 'POS';
  const isNone = srcRaw === 'NONE' || !srcRaw;

  const recon = truthSurface.sales_reconciliation || {};
  const reconciled = recon.reconciled === true;
  const reconInvalid = recon.state === 'INVALID';

  const containerCls = reconInvalid
    ? 'bg-red-500/8 border-red-500/30'
    : isMirror
    ? 'bg-emerald-500/8 border-emerald-500/25'
    : isLive
    ? 'bg-blue-500/8 border-blue-500/25'
    : isUpload
    ? 'bg-amber-500/8 border-amber-500/25'
    : isPos
    ? 'bg-emerald-500/8 border-emerald-500/25'
    : 'bg-zinc-500/8 border-zinc-500/25';

  const labelCls = reconInvalid
    ? 'text-red-400'
    : isMirror
    ? 'text-emerald-400'
    : isLive
    ? 'text-blue-400'
    : isUpload
    ? 'text-amber-400'
    : isPos
    ? 'text-emerald-400'
    : 'text-zinc-400';

  const humanSource = isMirror && srcRaw.includes('+SALES_CAPTURE')
    ? 'SC_TRANSACTIONS (mirror + live gap-fill)'
    : isMirror
    ? 'SC_TRANSACTIONS (canonical mirror)'
    : srcRaw;

  const bridges = truthSurface.bridges || {};
  const tol = recon.tolerance ?? 1;
  const rows = [
    {
      label: 'Core Sales',
      lhs: bridges.core_sales,
      rhsLabel: 'Gross − Discounts',
      rhs: bridges.gross_minus_discount,
      delta: (bridges.core_sales || 0) - (bridges.gross_minus_discount || 0),
    },
    {
      label: 'Category Sales',
      lhs: bridges.category_total,
      rhsLabel: 'Core Sales',
      rhs: bridges.core_sales,
      delta: (bridges.category_total || 0) - (bridges.core_sales || 0),
    },
    {
      label: 'Net Sales',
      lhs: bridges.net_sales,
      rhsLabel: 'Core + Packaging',
      rhs: bridges.core_plus_packaging,
      delta: (bridges.net_sales || 0) - (bridges.core_plus_packaging || 0),
    },
    {
      label: 'Customer Collection',
      lhs: bridges.customer_collection,
      rhsLabel: 'Net + Service + Tax',
      rhs: bridges.net_plus_service_plus_tax,
      delta: (bridges.customer_collection || 0) - (bridges.net_plus_service_plus_tax || 0),
    },
  ];
  const rowsOk = rows.filter((r) => Math.abs(r.delta) <= tol).length;

  return (
    <div className={`space-y-2`}>
      <div
        data-testid={`${testIdPrefix}-truth-strip`}
        className={`px-3 py-2 rounded-lg border ${containerCls}`}
      >
        <div className="flex items-center justify-between flex-wrap gap-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[11px] font-medium ${labelCls}`} data-testid={`${testIdPrefix}-sales-source`}>
              Sales Source: {humanSource}
            </span>
            {!isNone &&
              (reconInvalid ? (
                <span
                  data-testid={`${testIdPrefix}-reconciliation-badge`}
                  className="inline-flex items-center gap-1 text-[10px] font-medium text-red-300 bg-red-500/15 border border-red-500/30 px-1.5 py-0.5 rounded-full"
                  title={recon.reason || 'PNL_RECONCILIATION_FAILED'}
                >
                  <AlertTriangle className="w-3 h-3" />
                  Reconciliation Failed
                </span>
              ) : reconciled ? (
                <span
                  data-testid={`${testIdPrefix}-reconciliation-badge`}
                  className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-1.5 py-0.5 rounded-full"
                  title={`Tolerance ${fmt(tol)} · deltas: core ${recon.deltas?.core_sales ?? 0}, net ${recon.deltas?.net_sales ?? 0}, collection ${recon.deltas?.collection ?? 0}`}
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Reconciled
                </span>
              ) : null)}
          </div>
        </div>
        {reconInvalid && (
          <div
            data-testid={`${testIdPrefix}-reconciliation-failed-banner`}
            className="mt-1.5 pt-1.5 border-t border-red-500/20 text-[10px] text-red-200"
          >
            <div className="font-medium">Data inconsistent — not decision-grade.</div>
            <div className="text-red-300/80">
              Sales, category splits, and collections do not reconcile to the same truth source.
            </div>
          </div>
        )}
      </div>

      {!compact && (
        <details
          data-testid={`${testIdPrefix}-reconciliation-panel`}
          className={`rounded-lg border ${reconInvalid ? 'bg-red-500/5 border-red-500/25' : 'bg-emerald-500/5 border-emerald-500/20'}`}
        >
          <summary className="px-3 py-2 cursor-pointer text-[11px] flex items-center justify-between select-none">
            <span className="flex items-center gap-2">
              {reconInvalid ? (
                <AlertTriangle className="w-3 h-3 text-red-400" />
              ) : (
                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              )}
              <span className={reconInvalid ? 'text-red-300 font-medium' : 'text-emerald-300 font-medium'}>
                Reconciliation {reconInvalid ? 'Failed' : 'Passed'}
              </span>
              <span className="text-zinc-500">
                · {rowsOk}/{rows.length} bridges within tolerance {fmt(tol)}
              </span>
            </span>
            <span className="text-[9px] text-zinc-500">click to inspect</span>
          </summary>
          <div className="px-3 pb-2 pt-1 text-[10px]">
            <div className="space-y-1">
              {rows.map((r) => {
                const ok = Math.abs(r.delta) <= tol;
                return (
                  <div
                    key={r.label}
                    data-testid={`${testIdPrefix}-bridge-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="grid grid-cols-[8rem_1fr_auto_1fr_auto] gap-2 items-center py-0.5 border-b border-zinc-800/40 last:border-b-0"
                  >
                    <span className="text-zinc-400">{r.label}</span>
                    <span className="font-mono text-zinc-200 text-right">{fmt(r.lhs)}</span>
                    <span className="text-zinc-600 text-[9px]">==</span>
                    <span className="text-zinc-500 text-right">{r.rhsLabel}</span>
                    <span className={`font-mono text-right min-w-[5rem] ${ok ? 'text-emerald-400' : 'text-red-400'}`}>
                      Δ {r.delta >= 0 ? '+' : ''}
                      {Number(r.delta).toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </details>
      )}
    </div>
  );
};

export default PulseTruthSurface;
